// Logik des Vorfilters „Sonstige Kontakte" — DOM-frei, damit sie in node
// getestet werden kann (wie `filterModel.ts`).
//
// „Sonstige" meint in der Tabelle alles, was weder Kunde noch Interessent ist.
// Der Vorfilter macht diese Gruppe adressierbar, und zwar SOFORT: ein Häkchen
// heißt „diese Gruppe steht in der Liste", ein entferntes Häkchen blendet sie
// aus. Der zusätzliche Schalter dreht die Auswahl zu einem Exklusiv-Filter um
// („nur noch diese Gruppen").
//
// Warum nicht umgekehrt (Häkchen = einblenden eines sonst versteckten Rests):
// dann wäre die Kontaktliste beim ersten Öffnen bereits gefiltert, ohne dass
// jemand etwas eingestellt hätte. Alle Häkchen sitzen deshalb im Auslieferungs-
// zustand — die Liste zeigt zunächst alles, genau wie ohne den Vorfilter.

import {
  ALLE_ROLLEN_OPTIONS,
  GRUPPE_RISIKO,
  GRUPPE_SCHADEN,
  GRUPPE_VERTRAG,
  rollenWerteDerGruppe,
} from "../../../data/rollenKatalog";
import { aktuellerRollenKatalog } from "../../../data/kontaktRollen";
import {
  STATUS_EINTRAEGE_FIRMA,
  zuAuswahlOptionen,
} from "../../../data/statusAuswahlKatalog";

/** Ein wählbarer Status im Popup. */
export type SonstigeOption = {
  /** Der Wert, wie er in `ContactStatus.status` steht. */
  status: string;
  label: string;
};

/**
 * Die Status, die in der Kontaktliste eine EIGENE Kachel bekommen. Alles andere
 * zeigt die Liste als „Sonstige" — und genau das ist die Gruppe, die der
 * Spaltenfilter unter diesem Namen adressiert. Gegenstück zu
 * `KUNDENSTATUS_MIT_EIGENEM_CHIP` in `contact.service.ts`; wer hier etwas
 * ergänzt, muss es dort mitziehen, sonst laufen Anzeige und Filter auseinander.
 */
export const STATUS_MIT_EIGENEM_CHIP: string[] = ["Kunde", "Interessent"];

/**
 * Die Status, die der Vorfilter anbietet, in Anzeigereihenfolge — zugleich die
 * Bestandteile der Gruppe „Sonstige" im Spaltenfilter.
 *
 * Es ist der Katalog OHNE „Kunde"/„Interessent" (eigene Kachel), ohne
 * „Sonstige" (der Sammelbegriff selbst) und ohne „Unbekannt" (kein fachlicher
 * Status). Sortiert nach Sachgebiet: erst die Menschen an einem Unternehmen,
 * dann Haushalt und Altbestand, dann die Partner, dann Courté selbst.
 *
 * Die Werte sind dieselben wie in `data/sonstigeStatusKatalog.ts` (Anlage) und
 * `apps/api/src/contactStatus.katalog.ts` (Backend) — die Beschriftung steht
 * hier bewusst im Plural, weil sie eine GRUPPE von Kontakten benennt.
 */
export const SONSTIGE_OPTIONS: SonstigeOption[] = [
  { status: "Mensch im Unternehmen", label: "Menschen im Unternehmen" },
  // Bis 29.09.2026 standen hier zwei Zeilen: „Mensch fürs Unternehmen" (der
  // gespeicherte Wert) und „Verantwortung fürs Unternehmen" (zurückgezogen).
  // Seit Migration 202609290070 ist es EIN Wert; das Backend liest den alten
  // Namen als Alias, ein gespeicherter Filter darauf trifft weiter.
  { status: "Verantwortung fürs Unternehmen", label: "Verantwortung fürs Unternehmen" },
  { status: "Kontakt mit Beziehung zu einem Kontakt", label: "Kontakte mit Beziehung zu einem Kontakt" },
  { status: "Haushaltsmitglied", label: "Haushaltsmitglieder" },
  { status: "Altkunde", label: "Altkunden" },
  { status: "Produktpartner", label: "Produktpartner" },
  { status: "Kooperationspartner", label: "Kooperationspartner" },
  { status: "Ansprechpartner Produktpartner", label: "Ansprechpartner Produktpartner" },
  { status: "Ansprechpartner Kooperationspartner", label: "Ansprechpartner Kooperationspartner" },
  { status: "Direktionspartner", label: "Direktionspartner" },
  { status: "Vermittler, Tippgeber", label: "Vermittler, Tippgeber" },
  { status: "Empfehlungsgeber", label: "Empfehlungsgeber" },
  { status: "Lieferant", label: "Lieferanten" },
  { status: "Sponsoring Partner", label: "Sponsoring Partner" },
  { status: "Mitgliedschaft/Verband", label: "Mitgliedschaften, Verbände" },
  { status: "Mitarbeiter Courté", label: "Mitarbeiter Courté" },
  // Auswahlliste des Privatkontakts, 14.09.2026. Sie stehen hier, weil der
  // Vorfilter JEDEN Wert aus SONSTIGE_KONTAKTSTATUS führen muss — sonst fiele
  // ein so erfasster Kontakt aus der Gruppe „Sonstige" heraus und wäre über
  // den Spaltenfilter nicht mehr zu finden (`sonstigeKontaktstatus.spec.ts`).
  { status: "Altinteressent", label: "Altinteressenten" },
  { status: "Lieferant/Dienstleister", label: "Lieferanten, Dienstleister" },
  { status: "Immobilienbezogener Kontakt", label: "Immobilienbezogene Kontakte" },
  { status: "Sponsor/Spender", label: "Sponsoren, Spender" },
  { status: "Verein/Verband", label: "Vereine, Verbände" },
];

/**
 * Wert des Spaltenfilters „Kontaktstatus" für die GRUPPE Sonstige — alles, was
 * weder Kunde noch Interessent ist. Kein echter Status, deshalb ein Wert, der
 * im Katalog nicht vorkommen kann; das Frontend übersetzt ihn in den
 * Backend-Schlüssel `KontaktstatusGruppe`.
 */
export const SONSTIGE_GRUPPE_WERT = "__sonstige__";

/** Backend-Schlüssel und -Wert für die Gruppe (siehe `contact.service.ts`). */
export const SONSTIGE_GRUPPE_FILTER = { key: "KontaktstatusGruppe", value: "sonstige" } as const;

/**
 * Die drei Rollen-Gruppen als Sammelzeilen des Spaltenfilters.
 *
 * Eine Rolle ist ein ganz normaler Kontaktstatus, also einzeln filterbar. Zu
 * dritt sind es aber 32 Einträge — ohne die Sammelzeilen wäre die Liste
 * unbedienbar. Die Gruppenwerte sind KEINE Status: sie reisen als
 * `KontaktstatusIn` ans Backend, wie der Vorfilter es auch tut.
 *
 * Achtung bei „Halter": er steht in zwei Gruppen. Die Filterzeile erscheint
 * trotzdem nur einmal — unter der ersten (Schaden). Wer nach Kfz-Haltern
 * sucht, findet dieselben Kontakte.
 */
export const ROLLEN_GRUPPEN_FILTER: { wert: string; label: string; status: string[] }[] = [
  { wert: "__rolle_schaden__", label: "Rolle im Schaden — alle", status: rollenWerteDerGruppe(GRUPPE_SCHADEN) },
  {
    wert: "__rolle_risiko__",
    label: "Rolle am Risiko — alle",
    // Ohne die bereits beim Schaden geführten Werte: sonst stünde „Halter"
    // zweimal in der Auswahl.
    status: rollenWerteDerGruppe(GRUPPE_RISIKO).filter(
      (v) => !rollenWerteDerGruppe(GRUPPE_SCHADEN).includes(v),
    ),
  },
  { wert: "__rolle_vertrag__", label: "Rolle im Vertrag — alle", status: rollenWerteDerGruppe(GRUPPE_VERTRAG) },
];

/**
 * Die Header-Blöcke der Firmen-Auswahl als Sammelzeilen.
 *
 * Dieselbe Überlegung wie bei den Rollen-Gruppen: „Lieferant/Dienstleister"
 * bringt allein 29 Werte mit — einzeln wäre die Filterliste unbedienbar. Die
 * Überschrift ist kein Status; sie reist als `KontaktstatusIn` ans Backend.
 *
 * Erzeugt aus derselben Quelle wie die Auswahlliste: Wächst dort ein Block,
 * wächst hier die Sammelzeile mit, statt auseinanderzulaufen.
 */
export const FIRMEN_GRUPPEN_FILTER: { wert: string; label: string; status: string[] }[] =
  STATUS_EINTRAEGE_FIRMA.filter((e) => e.details).map((e) => ({
    wert: `__firma_${e.label.replace(/[^a-zA-ZäöüÄÖÜß]+/g, "_").toLowerCase()}__`,
    label: `${e.label} — alle`,
    status: (e.details ?? []).map((d) => d.value),
  }));

/** Jeder Wert, den die Firmen-Auswahl anbietet — für den Spaltenfilter. */
export const FIRMEN_STATUS_WERTE: string[] = Array.from(
  new Set(zuAuswahlOptionen(STATUS_EINTRAEGE_FIRMA).map((o) => o.value)),
);

/** Die Sammelzeilen selbst — sie sind Gruppen, keine Detailwerte. */
const ROLLEN_GRUPPEN_WERTE: ReadonlySet<string> = new Set(
  ROLLEN_GRUPPEN_FILTER.map((g) => g.wert),
);

/**
 * Die Auswahl des Spaltenfilters „Kontaktstatus" in Anzeigereihenfolge.
 *
 * Oben die beiden Status mit eigener Kachel, dann die Gruppe, dann ihre
 * Bestandteile einzeln. Der Katalogwert „Sonstige" steht dabei AUCH einzeln
 * darin: ohne ihn ließen sich die Kontakte, die wörtlich „Sonstige" heißen,
 * nicht mehr von der Gruppe trennen — heute geht das, und es soll nicht
 * verloren gehen. „Unbekannt" fehlt bewusst: er ist kein fachlicher Status,
 * sondern die Abwesenheit eines solchen, und über die Gruppe erreichbar.
 */
export const KONTAKTSTATUS_FILTER_OPTIONS: SonstigeOption[] = [
  ...STATUS_MIT_EIGENEM_CHIP.map((status) => ({ status, label: status })),
  { status: SONSTIGE_GRUPPE_WERT, label: "Sonstige — alles Übrige" },
  ...SONSTIGE_OPTIONS.map((o) => ({ status: o.status, label: o.label })),
  { status: "Sonstige", label: "nur „Sonstige“" },
  ...ROLLEN_GRUPPEN_FILTER.flatMap((g) => [
    { status: g.wert, label: g.label },
    ...ALLE_ROLLEN_OPTIONS.filter((r) => g.status.includes(r.value)).map((r) => ({
      status: r.value,
      label: r.label,
    })),
  ]),
];

/**
 * Die Einträge unterhalb der Gruppenzeile — sie werden im Popover eingerückt.
 * Aus der Liste abgeleitet statt per Index geschnitten, damit ein Umsortieren
 * die Einrückung nicht stillschweigend auf die falschen Zeilen legt.
 */
export const KONTAKTSTATUS_DETAIL_WERTE: ReadonlySet<string> = new Set(
  KONTAKTSTATUS_FILTER_OPTIONS.filter(
    (o) =>
      o.status !== SONSTIGE_GRUPPE_WERT &&
      !ROLLEN_GRUPPEN_WERTE.has(o.status) &&
      !STATUS_MIT_EIGENEM_CHIP.includes(o.status),
  ).map((o) => o.status),
);

/** Beschriftung zu einem Wert des Spaltenfilters. */
export const kontaktstatusFilterLabel = (wert: string): string =>
  KONTAKTSTATUS_FILTER_OPTIONS.find((o) => o.status === wert)?.label ?? wert;

/**
 * Der Anteil des Spaltenfilters am `filters`-Objekt der Kontaktliste.
 *
 * Jeder echte Status geht als Exakt-Vergleich (`Kontaktstatus`) ans Backend.
 * Die Gruppe ist kein Status, sondern deren Abwesenheit und reist deshalb unter
 * eigenem Schlüssel — sonst suchte das Backend nach einem Katalogeintrag
 * namens „__sonstige__" und die Liste bliebe leer.
 */
export const kontaktstatusFilterPayload = (
  wert: string | boolean | undefined,
): Record<string, unknown> => {
  if (typeof wert !== "string" || wert === "") return {};
  if (wert === SONSTIGE_GRUPPE_WERT) {
    return { [SONSTIGE_GRUPPE_FILTER.key]: SONSTIGE_GRUPPE_FILTER.value };
  }
  // Ein Bereich der Kontaktrollen (K-Status-Filter → Sonstige → Bereich).
  const bereich = kStatusBereichAusWert(wert);
  if (bereich !== null) {
    const status = rollenWerteDesBereichs(bereich);
    return status.length ? { KontaktstatusIn: status } : { [SONSTIGE_GRUPPE_FILTER.key]: SONSTIGE_GRUPPE_FILTER.value };
  }
  // Eine Rollen-Sammelzeile ist ebenfalls kein Status, sondern deren mehrere.
  const gruppe = ROLLEN_GRUPPEN_FILTER.find((g) => g.wert === wert);
  if (gruppe) return { KontaktstatusIn: gruppe.status };
  return { Kontaktstatus: wert };
};

const ALL_STATUS: string[] = SONSTIGE_OPTIONS.map((o) => o.status);

/**
 * Vorauswahl beim ersten Öffnen: ALLE angehakt. Erst dadurch ist der
 * Auslieferungszustand identisch mit „kein Vorfilter" — die Liste zeigt jeden
 * Kontakt, und jedes entfernte Häkchen ist eine bewusste Einschränkung.
 */
export const SONSTIGE_DEFAULT: string[] = [...ALL_STATUS];

export type SonstigeFilter = {
  /** Angehakte Status = in der Liste sichtbar. */
  selected: string[];
  /** Schalter „nur Sonstige Kontakte anzeigen". */
  nurSonstige: boolean;
};

export const SONSTIGE_INITIAL: SonstigeFilter = {
  selected: SONSTIGE_DEFAULT,
  nurSonstige: false,
};

/** Einen Status an-/abwählen; die Reihenfolge folgt dem Katalog, nicht dem Klick. */
export const toggleSonstige = (filter: SonstigeFilter, status: string): SonstigeFilter => {
  const next = new Set(filter.selected);
  if (next.has(status)) next.delete(status);
  else next.add(status);
  return {
    ...filter,
    selected: SONSTIGE_OPTIONS.filter((o) => next.has(o.status)).map((o) => o.status),
  };
};

/** Die abgewählten Status — genau die, die aus der Liste verschwinden sollen. */
export const sonstigeHidden = (filter: SonstigeFilter): string[] =>
  ALL_STATUS.filter((s) => !filter.selected.includes(s));

/**
 * Ob der Filter die Liste tatsächlich einschränkt — Grundlage für die
 * Hervorhebung des Buttons.
 *
 * Zwei Wege dorthin: der Schalter (dann zeigt die Liste nur noch die
 * angehakten Gruppen) oder ein entferntes Häkchen (dann fehlt diese Gruppe).
 * Ein eingeschalteter Schalter OHNE jedes Häkchen zählt bewusst nicht — er
 * erzeugte eine garantiert leere Liste, die niemand bestellt hat.
 */
export const isSonstigeActive = (filter: SonstigeFilter): boolean =>
  (filter.nurSonstige && filter.selected.length > 0) || sonstigeHidden(filter).length > 0;

/**
 * Der Anteil am `filters`-Objekt der Kontaktliste. Leeres Objekt heißt: dieser
 * Vorfilter mischt sich nicht ein.
 *
 * Der Schalter hat Vorrang — „nur diese Gruppen" schließt alles andere ohnehin
 * aus, ein zusätzliches `KontaktstatusNotIn` wäre redundant.
 */
export const sonstigeFilterPayload = (
  filter: SonstigeFilter,
): Record<string, unknown> => {
  if (filter.nurSonstige && filter.selected.length > 0) {
    return { KontaktstatusIn: filter.selected };
  }
  const hidden = sonstigeHidden(filter);
  return hidden.length > 0 ? { KontaktstatusNotIn: hidden } : {};
};

/**
 * Beschriftung des Buttons. Sie sagt, WAS der Filter gerade tut — „nur 3"
 * (Exklusiv-Filter) unterscheidet sich fundamental von „2 ausgeblendet", und
 * eine bloße Zahl ließe beides gleich aussehen.
 */
export const sonstigeButtonLabel = (filter: SonstigeFilter): string => {
  if (filter.nurSonstige && filter.selected.length > 0) {
    return `Sonstige Kontakte (nur ${filter.selected.length})`;
  }
  const hidden = sonstigeHidden(filter).length;
  return hidden > 0
    ? `Sonstige Kontakte (${hidden} ausgeblendet)`
    : "Sonstige Kontakte";
};

// ── K-Status-Filter: Kunde | Interessent | Sonstige → Bereich/Rolle ────────
//
// Seit 28.09.2026 bietet der Spaltenfilter nur noch die drei Kacheln an; wer
// „Sonstige" wählt, grenzt im selben Popover nach Bereich und Rolle ein — mit
// dem Katalog der Kontaktanlage (Einstellungen → Kontaktrollen). Ein Bereich
// ist kein Status, sondern die Menge seiner Rollen; er reist deshalb unter
// eigenem Präfix und wird hier in `KontaktstatusIn` übersetzt.

const BEREICH_PRAEFIX = "__bereich__:";

export const kStatusBereichWert = (titel: string): string => BEREICH_PRAEFIX + titel;

export const kStatusBereichAusWert = (wert: string): string | null =>
  wert.startsWith(BEREICH_PRAEFIX) ? wert.slice(BEREICH_PRAEFIX.length) : null;

/** Die Rollenwerte eines Bereichs im aktiven Katalog. */
export const rollenWerteDesBereichs = (titel: string): string[] =>
  aktuellerRollenKatalog().find((b) => b.titel === titel)?.rollen.map((r) => r.value) ?? [];

/** Zu welchem Bereich gehört eine Rolle (erster Treffer im aktiven Katalog)? */
export const bereichDerRolle = (rolle: string): string | null =>
  aktuellerRollenKatalog().find((b) => b.rollen.some((r) => r.value === rolle))?.titel ?? null;

/** Kurzbeschreibung des aktiven K-Status-Filters für das Popover. */
export const kStatusFilterBeschreibung = (wert: string | undefined): string => {
  if (!wert) return "";
  if (STATUS_MIT_EIGENEM_CHIP.includes(wert)) return wert;
  if (wert === SONSTIGE_GRUPPE_WERT) return "Sonstige — alle";
  const bereich = kStatusBereichAusWert(wert);
  if (bereich !== null) return `Sonstige · ${bereich}`;
  const rolle = aktuellerRollenKatalog()
    .flatMap((b) => b.rollen.map((r) => ({ ...r, bereich: b.titel })))
    .find((r) => r.value === wert);
  return rolle ? `Sonstige · ${rolle.bereich} · ${rolle.label}` : `Sonstige · ${wert}`;
};
